/** Human-readable duplicate of a raw `YYYY-MM-DD` date, for the visible line property. */
function formatVisibleDeliveryDate(dateStr) {
  try {
    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(`${dateStr}T12:00:00Z`));
  } catch {
    return dateStr;
  }
}

class DapFormBridge extends HTMLElement {
  #unsubSessionChange = null;
  #lastSyncedKey = undefined;
  #syncController = null;
  #syncInFlight = null;
  #syncInFlightKey = undefined;
  #lastLineSyncKey = undefined;
  #lineSyncInFlight = null;
  #lineSyncInFlightKey = undefined;
  #boundSubmitHandler = null;
  #dapActive = false;

  connectedCallback() {
    document.addEventListener("dap:session-complete", this.#onComplete);

    if (typeof window.DAP?.onSessionChange === "function") {
      this.#unsubSessionChange = window.DAP.onSessionChange((session) => {
        if (session) {
          this.#populateForms(session);
          this.#syncCartAttribute(session);
          this.#syncLineProperties(session);
          this.#updateCartCutoff(session);
          this.#setDapActive(true);
        } else {
          this.#clearForms();
          this.#syncCartAttribute(null);
          this.#updateCartCutoff(null);
          this.#setDapActive(false);
        }
      });
    }

    this.#boundSubmitHandler = this.#onCheckoutSubmit.bind(this);
    document.addEventListener("submit", this.#boundSubmitHandler, true);

    this.#hydrateFromSession();
  }

  disconnectedCallback() {
    document.removeEventListener("dap:session-complete", this.#onComplete);
    if (this.#boundSubmitHandler) {
      document.removeEventListener("submit", this.#boundSubmitHandler, true);
      this.#boundSubmitHandler = null;
    }
    if (this.#unsubSessionChange) {
      this.#unsubSessionChange();
      this.#unsubSessionChange = null;
    }
    this.#syncController?.abort();
    this.#syncController = null;
  }

  #onComplete = (e) => {
    this.#populateForms(e.detail);
    this.#syncCartAttribute(e.detail);
    this.#syncLineProperties(e.detail);
    this.#updateCartCutoff(e.detail);
    this.#setDapActive(true);
    this.#maybeAutoRedirect();
  };

  #maybeAutoRedirect() {
    const target = document
      .querySelector("[data-dap-section][data-dap-auto-redirect]")
      ?.getAttribute("data-dap-auto-redirect");
    if (!target) return;
    const here = window.location.pathname.replace(/\/+$/, "") || "/";
    if (here !== "/" && here !== "/index") return;
    window.location.assign(target);
  }

  #hydrateFromSession() {
    if (window.DAP?.isSessionValid()) {
      const session = window.DAP.getSession();
      this.#populateForms(session);
      this.#syncCartAttribute(session);
      this.#syncLineProperties(session);
      this.#updateCartCutoff(session);
      this.#setDapActive(true);
    } else {
      this.#clearForms();
      this.#syncCartAttribute(null);
      this.#updateCartCutoff(null);
      this.#setDapActive(false);
    }
  }

  /** Human-readable duplicate of `_delivery_date` shown to the customer; label comes from `data-delivery-date-label`. */
  #visibleDeliveryDateProp(session) {
    const label = this.dataset.deliveryDateLabel;
    if (!label || !session.date) return {};
    return { [label]: formatVisibleDeliveryDate(session.date) };
  }

  #populateForms(session) {
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    const map = {
      _delivery_date: session.date,
      _street_address: session.address,
      _postal_code: session.postcode,
      _city: session.city,
      _warehouse_location: session.location,
      _carrier: session.carrier_name,
      _handling_date: session.handling_date ?? "",
      _sameday_delivery: session.sameday_delivery ? "true" : "false",
      _sorting_station: session.sorting_station ?? "",
      _delivery_depot: session.delivery_depot ?? "",
      _zone: session.zone ?? "",
      ...this.#visibleDeliveryDateProp(session),
    };
    for (const form of forms) {
      for (const [key, value] of Object.entries(map)) {
        let input = form.querySelector(`input[name="properties[${key}]"]`);
        if (!input) {
          input = document.createElement("input");
          input.type = "hidden";
          input.name = `properties[${key}]`;
          form.appendChild(input);
        }
        input.value = value ?? "";
      }
    }
  }

  async #syncCartAttribute(session) {
    const addressType =
      session?.address_type === "personal" || session?.address_type === "company"
        ? session.address_type
        : "";
    const attrs = {
      _dap_location_id: session?.location_id ?? "",
      _dap_address_type: addressType,
      _dap_carrier_id: session?.carrier_id ?? "",
      _dap_delivery_date: session?.date ?? "",
      _dap_warehouse: session?.warehouse ?? "",
      _dap_warehouse_location: session?.location ?? "",
      _dap_postcode: session?.postcode ?? "",
      _dap_address1: session?.address ?? "",
      _dap_city: session?.city ?? "",
      _dap_handling_date: session?.handling_date ?? "",
      _dap_sameday_delivery: session?.sameday_delivery ? "true" : "false",
      _dap_handling_cutoff: session?.handling_cutoff ?? "",
      _dap_sorting_station: session?.sorting_station ?? "",
      _dap_delivery_depot: session?.delivery_depot ?? "",
      _dap_zone: session?.zone ?? "",
      _dap_line_overrides: "{}",
    };
    const key = JSON.stringify(attrs);
    if (key === this.#lastSyncedKey) return;
    if (this.#syncInFlightKey === key && this.#syncInFlight) {
      return this.#syncInFlight;
    }

    this.#syncController?.abort();
    this.#syncController = new AbortController();
    const controller = this.#syncController;

    this.#syncInFlightKey = key;
    this.#syncInFlight = (async () => {
      const isCurrentSync = () =>
        this.#syncInFlightKey === key &&
        this.#syncController === controller &&
        !controller.signal.aborted;

      try {
        const resp = await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: attrs }),
          signal: controller.signal,
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        if (isCurrentSync()) {
          this.#lastSyncedKey = key;
        }
      } catch {
        // Keep the previous successful key so a failed/aborted attempt can retry.
      } finally {
        if (this.#syncInFlightKey === key) {
          this.#syncInFlight = null;
          this.#syncInFlightKey = undefined;
        }
      }
    })();

    return this.#syncInFlight;
  }

  #dapLinePropertiesFromSession(session) {
    return {
      _delivery_date: session.date ?? "",
      _street_address: session.address ?? "",
      _postal_code: session.postcode ?? "",
      _city: session.city ?? "",
      _warehouse_location: session.location ?? "",
      _carrier: session.carrier_name ?? "",
      _handling_date: session.handling_date ?? "",
      _sameday_delivery: session.sameday_delivery ? "true" : "false",
      _sorting_station: session.sorting_station ?? "",
      _delivery_depot: session.delivery_depot ?? "",
      _zone: session.zone ?? "",
      ...this.#visibleDeliveryDateProp(session),
    };
  }

  #lineHasDapProps(properties) {
    if (!properties || typeof properties !== "object") return false;
    const handling = properties._handling_date;
    const delivery = properties._delivery_date;
    const warehouse = properties._warehouse_location;
    return (
      (typeof handling === "string" && handling.length > 0) ||
      (typeof delivery === "string" && delivery.length > 0) ||
      (typeof warehouse === "string" && warehouse.length > 0)
    );
  }

  #lineNeedsDapSync(item, dapProps) {
    const props = item.properties || {};
    if (!this.#lineHasDapProps(props)) return false;
    return Object.entries(dapProps).some(
      ([key, value]) => String(props[key] ?? "") !== String(value ?? ""),
    );
  }

  /**
   * Re-sync DAP line item properties after session changes so bouquet lines
   * do not keep frozen ATC values while cart attributes update.
   * Sequential /cart/change.js with full property merge (Shopify replaces, not merges).
   * Dedupes concurrent setSession + dap:session-complete callers.
   */
  async #syncLineProperties(session) {
    if (!session) return;

    const dapProps = this.#dapLinePropertiesFromSession(session);
    const key = JSON.stringify(dapProps);
    if (key === this.#lastLineSyncKey) return;
    if (this.#lineSyncInFlightKey === key && this.#lineSyncInFlight) {
      return this.#lineSyncInFlight;
    }

    this.#lineSyncInFlightKey = key;
    this.#lineSyncInFlight = (async () => {
      // A newer session takes over the key; stale loops must stop writing.
      const isCurrentSync = () => this.#lineSyncInFlightKey === key;

      try {
        const cartResp = await fetch("/cart.js");
        if (!cartResp.ok || !isCurrentSync()) return;
        let cart = await cartResp.json();

        for (let guard = 0; guard < 20; guard++) {
          if (!isCurrentSync()) return;

          const items = Array.isArray(cart.items) ? cart.items : [];
          const idx = items.findIndex((item) => this.#lineNeedsDapSync(item, dapProps));
          if (idx < 0) {
            this.#lastLineSyncKey = key;
            return;
          }

          const item = items[idx];
          const merged = { ...(item.properties || {}), ...dapProps };
          const changeResp = await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              line: idx + 1,
              quantity: item.quantity,
              properties: merged,
            }),
          });
          if (!changeResp.ok) return;

          // Line keys change when properties update, so continue from the
          // cart the change returned rather than re-fetching or caching keys.
          cart = await changeResp.json();
        }
      } catch {
        /* cart sync is best-effort; Function line/cart consistency is the backstop */
      } finally {
        if (this.#lineSyncInFlightKey === key) {
          this.#lineSyncInFlight = null;
          this.#lineSyncInFlightKey = undefined;
        }
      }
    })();

    return this.#lineSyncInFlight;
  }

  #setDapActive(active) {
    this.#dapActive = active;
    this.#toggleAcceleratedButtons(active);
  }

  #toggleAcceleratedButtons(disabled) {
    const selectors = [
      ".additional-checkout-buttons",
      "[data-shopify='payment-button']",
    ];
    for (const sel of selectors) {
      for (const el of document.querySelectorAll(sel)) {
        el.setAttribute("aria-disabled", String(disabled));
        el.style.pointerEvents = disabled ? "none" : "";
        el.style.opacity = disabled ? "0.4" : "";

        for (const btn of el.querySelectorAll(".shopify-payment-button__button, button")) {
          if (disabled) {
            btn.setAttribute("disabled", "disabled");
            btn.setAttribute("tabindex", "-1");
          } else {
            btn.removeAttribute("disabled");
            btn.removeAttribute("tabindex");
          }
        }
      }
    }
  }

  async #onCheckoutSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const submitter = e.submitter;
    if (
      !(submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) ||
      submitter.name !== "checkout"
    ) {
      return;
    }
    if (!this.#dapActive) return;

    e.preventDefault();

    const btn = submitter;
    btn.disabled = true;

    const existing = form.querySelector("[data-dap-checkout-error]");
    if (existing) existing.remove();

    const session = window.DAP?.getSession();
    const addrType =
      session?.address_type === "personal" || session?.address_type === "company"
        ? session.address_type
        : "";
    const params = new URLSearchParams({
      postcode: session?.postcode ?? "",
      carrier_id: session?.carrier_id ?? "",
      delivery_date: session?.date ?? "",
      address_type: addrType,
    });

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const resp = await fetch(`/apps/delivery/validate?${params}`, {
        signal: controller.signal,
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      if (data.valid) {
        if (session) {
          const updated = {
            ...session,
            handling_date: data.handling_date ?? session.handling_date,
            sameday_delivery: data.sameday_delivery ?? session.sameday_delivery,
            carrier_id: data.carrier_ids ?? session.carrier_id,
            sorting_station: data.sorting_station ?? session.sorting_station ?? "",
            delivery_depot: data.delivery_depot ?? session.delivery_depot ?? "",
            zone: data.zone ?? session.zone ?? "",
            handling_cutoff: data.handling_cutoff ?? session.handling_cutoff ?? null,
          };
          this.#populateForms(updated);
          await this.#syncCartAttribute(updated);
          await this.#syncLineProperties(updated);
          window.DAP?.setSession(updated);
        }

        btn.disabled = false;
        const hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "checkout";
        hidden.value = "";
        form.appendChild(hidden);
        form.submit();
        return;
      }

      this.#showCheckoutError(form, data.reason || "Validation failed");
    } catch {
      this.#showCheckoutError(
        form,
        "Could not verify delivery details. Please try again.",
      );
    } finally {
      clearTimeout(timeout);
    }

    btn.disabled = false;
  }

  #showCheckoutError(form, message) {
    let el = form.querySelector("[data-dap-checkout-error]");
    if (!el) {
      el = document.createElement("div");
      el.setAttribute("data-dap-checkout-error", "");
      el.setAttribute("role", "alert");
      form.appendChild(el);
    }
    el.textContent = message;
  }

  #updateCartCutoff(session) {
    const el = document.querySelector("[data-dap-cart-cutoff]");
    if (!el) return;

    const isDeliveryToday = session?.location?.endsWith("_0");
    if (isDeliveryToday && session?.sameday_delivery && session?.same_day_cutoff) {
      const template = el.dataset.cutoffTemplate || "Order before __TIME__ for same-day delivery";
      el.textContent = template.replace("__TIME__", session.same_day_cutoff);
      el.hidden = false;
    } else {
      el.textContent = "";
      el.hidden = true;
    }
  }

  #clearForms() {
    const prefixes = [
      "properties[_delivery_",
      "properties[_street_",
      "properties[_postal_",
      "properties[_city",
      "properties[_warehouse_",
      "properties[_carrier",
      "properties[_handling_",
      "properties[_sameday_",
      "properties[_sorting_",
      "properties[_zone",
    ];
    for (const prefix of prefixes) {
      const inputs = document.querySelectorAll(
        `input[name^="${prefix}"]`,
      );
      for (const input of inputs) input.value = "";
    }
  }
}

if (typeof customElements !== "undefined") {
  if (!customElements.get("dap-form-bridge")) {
    customElements.define("dap-form-bridge", DapFormBridge);
  }
}
